import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Field,
  Modal,
  PageHeader,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  useToast,
} from '@catering/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, ShieldOff, Store } from 'lucide-react';
import { type FormEvent, type JSX, useState } from 'react';

import { hasPermission } from '../../auth/has-permission';
import { useCurrentUser } from '../../auth/use-current-user';
import { listFiscalPointsOfSale } from '../../invoices/fiscal-points-of-sale-api';
import {
  type OperatingUnit,
  createOperatingUnit,
  listOperatingUnits,
  mapOperatingUnitError,
  updateOperatingUnit,
} from '../../operating-units/operating-units-api';

/** `null` is the create form; an `OperatingUnit` is the edit form. */
type FormTarget = 'new' | OperatingUnit | null;

interface PointOfSaleFormModalProps {
  unit: OperatingUnit | null;
  canLinkFiscal: boolean;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}

function PointOfSaleFormModal({
  unit,
  canLinkFiscal,
  onCancel,
  onSaved,
}: PointOfSaleFormModalProps): JSX.Element {
  const [name, setName] = useState(unit?.name ?? '');
  const [address, setAddress] = useState(unit?.address ?? '');
  const [fiscalId, setFiscalId] = useState(unit?.fiscalPointOfSaleId ?? '');

  // The fiscal decision needs `fiscal.config`: without it the catalog is never requested.
  const fiscalQuery = useQuery({
    queryKey: ['fiscal-points-of-sale'],
    queryFn: listFiscalPointsOfSale,
    enabled: canLinkFiscal,
  });

  // A fiscal PV links to a single active unit: offer the free ones and this unit's own.
  const fiscalOptions = (fiscalQuery.data ?? []).filter(
    (point) =>
      point.id === unit?.fiscalPointOfSaleId ||
      (point.isActive && (point.operatingUnit === null || point.operatingUnit.id === unit?.id)),
  );

  const saveMutation = useMutation({
    mutationFn: () => {
      const trimmedAddress = address.trim();
      const link =
        canLinkFiscal && fiscalId !== (unit?.fiscalPointOfSaleId ?? '')
          ? { fiscalPointOfSaleId: fiscalId === '' ? null : fiscalId }
          : {};

      return unit === null
        ? createOperatingUnit({
            name: name.trim(),
            ...(trimmedAddress === '' ? {} : { address: trimmedAddress }),
            ...link,
          })
        : updateOperatingUnit(unit.id, {
            name: name.trim(),
            address: trimmedAddress === '' ? null : trimmedAddress,
            ...link,
          });
    },
    onSuccess: onSaved,
  });

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    saveMutation.mutate();
  }

  return (
    <Modal
      open
      onClose={onCancel}
      title={unit === null ? 'Nuevo punto de venta' : 'Editar punto de venta'}
      className="max-w-md"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        {saveMutation.isError ? (
          <Alert tone="danger">{mapOperatingUnitError(saveMutation.error)}</Alert>
        ) : null}

        <Field
          label="Nombre"
          name="name"
          required
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <Field
          label="Dirección"
          name="address"
          value={address}
          onChange={(event) => setAddress(event.target.value)}
        />

        <div className="flex flex-col gap-1.5">
          <label htmlFor="operating-unit-fiscal" className="text-sm font-medium text-slate-900">
            PV fiscal (ARCA)
          </label>
          <select
            id="operating-unit-fiscal"
            value={fiscalId}
            disabled={!canLinkFiscal}
            onChange={(event) => setFiscalId(event.target.value)}
            className="h-11 rounded-md border border-slate-300 px-3 text-sm disabled:bg-slate-100"
          >
            <option value="">Sin PV fiscal</option>
            {fiscalOptions.map((point) => (
              <option key={point.id} value={point.id}>
                {point.number} · {point.description}
              </option>
            ))}
          </select>
          {canLinkFiscal ? null : (
            <p className="text-xs text-slate-500">
              Vincular un PV fiscal requiere el permiso de configuración fiscal.
            </p>
          )}
        </div>

        <div className="flex items-center gap-3">
          <Button type="submit" loading={saveMutation.isPending} disabled={name.trim() === ''}>
            Confirmar
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * `points-of-sale/spec.md` + `design.md` §9. Gated on `settings.manage` here
 * (the server enforces it too): the page renders a 403 state instead of the
 * table, and never requests the catalog. The fiscal PV selector is editable
 * only with `fiscal.config`.
 */
export function PointOfSaleConfigPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [form, setForm] = useState<FormTarget>(null);

  const currentUserQuery = useCurrentUser();
  const canManage = hasPermission(currentUserQuery.data, 'settings.manage');
  const canLinkFiscal = hasPermission(currentUserQuery.data, 'fiscal.config');

  const query = useQuery({
    queryKey: ['operating-units'],
    queryFn: listOperatingUnits,
    enabled: canManage,
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      updateOperatingUnit(id, { isActive }),
    onSuccess: async (_unit, { isActive }) => {
      await queryClient.invalidateQueries({ queryKey: ['operating-units'] });
      toast('success', isActive ? 'Punto de venta activado' : 'Punto de venta desactivado');
    },
    onError: (error) => toast('error', mapOperatingUnitError(error)),
  });

  if (currentUserQuery.isPending) {
    return <Skeleton className="h-10 w-full" />;
  }

  if (!canManage) {
    return (
      <EmptyState
        icon={ShieldOff}
        title="No tenés permiso para ver esta sección"
        description="Pedile a un administrador que te otorgue el permiso de configuración."
      />
    );
  }

  const units = query.data ?? [];

  function toggleActive(unit: OperatingUnit): void {
    if (
      unit.isActive &&
      !window.confirm(
        `¿Desactivar "${unit.name}"? Deja de aparecer para elegir en todas las pantallas.`,
      )
    ) {
      return;
    }

    toggleActiveMutation.mutate({ id: unit.id, isActive: !unit.isActive });
  }

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={Store}
        title="Puntos de venta"
        description="Administrá las unidades operativas, su dirección y el PV fiscal con el que facturan."
        actions={
          <Button icon={Plus} onClick={() => setForm('new')}>
            Nuevo punto de venta
          </Button>
        }
      />

      {query.isError ? (
        <Alert tone="danger" title="No pudimos cargar los puntos de venta">
          <div className="flex items-center justify-between gap-4">
            <span>Ocurrió un error al buscar. Intentá de nuevo.</span>
            <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {query.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : null}

      {query.data !== undefined && units.length === 0 ? (
        <EmptyState
          icon={Store}
          title="Todavía no hay puntos de venta"
          description="Creá el primero con el botón Nuevo punto de venta."
        />
      ) : null}

      {query.data !== undefined && units.length > 0 ? (
        <Table>
          <caption className="sr-only">Puntos de venta</caption>
          <thead>
            <tr>
              <TableHeader>Nombre</TableHeader>
              <TableHeader>Dirección</TableHeader>
              <TableHeader>PV fiscal</TableHeader>
              <TableHeader>Contratos activos</TableHeader>
              <TableHeader>Estado</TableHeader>
              <TableHeader>Acciones</TableHeader>
            </tr>
          </thead>
          <TableBody>
            {units.map((unit) => (
              <TableRow key={unit.id}>
                <TableCell>{unit.name}</TableCell>
                <TableCell>{unit.address ?? '—'}</TableCell>
                <TableCell>
                  {unit.fiscalPointOfSale === null ? (
                    <Badge variant="warning">Sin PV fiscal</Badge>
                  ) : (
                    `${unit.fiscalPointOfSale.number} · ${unit.fiscalPointOfSale.description}`
                  )}
                </TableCell>
                <TableCell>{unit.activeContractsCount}</TableCell>
                <TableCell>
                  <Badge variant={unit.isActive ? 'success' : 'neutral'}>
                    {unit.isActive ? 'Activo' : 'Inactivo'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button variant="secondary" size="sm" onClick={() => setForm(unit)}>
                      Editar
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={
                        toggleActiveMutation.isPending &&
                        toggleActiveMutation.variables?.id === unit.id
                      }
                      onClick={() => toggleActive(unit)}
                    >
                      {unit.isActive ? 'Desactivar' : 'Activar'}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}

      {form === null ? null : (
        <PointOfSaleFormModal
          unit={form === 'new' ? null : form}
          canLinkFiscal={canLinkFiscal}
          onCancel={() => setForm(null)}
          onSaved={async () => {
            setForm(null);
            await queryClient.invalidateQueries({ queryKey: ['operating-units'] });
            await queryClient.invalidateQueries({ queryKey: ['fiscal-points-of-sale'] });
            toast('success', 'Punto de venta guardado');
          }}
        />
      )}
    </section>
  );
}
